import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export const fetchOrders = createAsyncThunk(
  'orders/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/orders');
      const orders = data.orders || data || [];

      return (Array.isArray(orders) ? orders : []).map((order) => ({
        ...order,
        status: order.status || order.state,
      }));
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch orders');
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'orders/fetchById',
  async (orderId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      const order = data.order || data;

      return {
        ...order,
        status: order.status || order.state,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch order');
    }
  }
);

export const createOrder = createAsyncThunk(
  'orders/create',
  async ({ shippingAddress, billingAddress, notes, items }, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/orders', {
        order: {
          shipping_address: shippingAddress,
          billing_address: billingAddress,
          notes,
          items: items.map(item => ({
            product_id: item.productId,
            variant_id: item.variantId || null,
            design_id: item.designId || null,
            quantity: item.quantity,
            customizations: item.customizations || {},
          })),
        },
      });
      return data.order || data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to create order');
    }
  }
);

export const initializePayment = createAsyncThunk(
  'orders/initializePayment',
  async ({ orderId, paymentMethod, callbackUrl }, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/payments/initialize', {
        order_id: orderId,
        payment_method: paymentMethod || 'creditcard',
        callback_url: callbackUrl,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to initialize payment');
    }
  }
);

export const verifyPayment = createAsyncThunk(
  'orders/verifyPayment',
  async ({ orderId, paymentId }, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/payments/verify', {
        order_id: orderId,
        payment_id: paymentId,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to verify payment');
    }
  }
);

export const cancelOrder = createAsyncThunk(
  'orders/cancel',
  async (orderId, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/orders/${orderId}/cancel`);
      const cancelled = data.order || data;

      return {
        ...cancelled,
        status: cancelled.status || cancelled.state,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to cancel order');
    }
  }
);

const initialState = {
  items: [],
  currentOrder: null,
  paymentData: null,
  verificationResult: null,
  loading: false,
  error: null,
};

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
      state.paymentData = null;
      state.verificationResult = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch By ID
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Order
      .addCase(createOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.items.unshift(action.payload);
        state.currentOrder = action.payload;
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Initialize Payment
      .addCase(initializePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializePayment.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentData = action.payload;
      })
      .addCase(initializePayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Verify Payment
      .addCase(verifyPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.verificationResult = action.payload;
      })
      .addCase(verifyPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Cancel Order
      .addCase(cancelOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload;
        // Update the order in the items list if present
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentOrder, clearError } = ordersSlice.actions;
export default ordersSlice.reducer;
